import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, KeyRound, Lock, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";

/**
 * iter154 — Password-reset audit tool.
 *
 * Support-only tool for the "user reset but still can't log in" case.
 * Type in a user email → the backend returns everything a support admin
 * needs to answer "why?" in one shot:
 *   • how many user docs match the email
 *   • whether each doc has a valid-looking password hash
 *   • the last 10 reset tokens for that email
 *   • whether each token's `user_id` still resolves to a user doc
 *     (catches the "orphan token" / "wrong user_id" failure mode)
 *   • a plain-English `diagnosis` label the UI colour-codes
 *
 * Never returns the raw password hash — only a 4-char prefix + length
 * fingerprint so we can confirm "yes it's a bcrypt hash" without
 * leaking the value.
 */
export default function UserResetAuditCard() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [sendResetBusy, setSendResetBusy] = useState(false);
  // iter154 — direct set-password dialog state
  const [setPwOpen, setSetPwOpen] = useState(false);
  const [setPwTarget, setSetPwTarget] = useState(null); // {id, email}
  const [setPwValue, setSetPwValue] = useState("");
  const [setPwBusy, setSetPwBusy] = useState(false);

  const runAudit = async (e) => {
    e?.preventDefault?.();
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error("Enter an email to look up.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get(
        `/admin/users/reset-audit?email=${encodeURIComponent(trimmed)}`,
      );
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const sendReset = async (userId) => {
    setSendResetBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${userId}/send-reset`);
      toast.success(
        `Reset link sent to ${data.sent_to} — expires in ${data.expires_in_minutes} min`,
      );
      // Refresh the audit so the new token appears at the top of the list
      await runAudit();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendResetBusy(false);
    }
  };

  const openSetPw = (user) => {
    setSetPwTarget({ id: user.id, email: user.email });
    setSetPwValue("");
    setSetPwOpen(true);
  };

  const confirmSetPw = async () => {
    if (!setPwTarget?.id) return;
    if (setPwValue.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setSetPwBusy(true);
    try {
      await api.post(`/admin/users/${setPwTarget.id}/set-password`, {
        new_password: setPwValue,
      });
      toast.success(
        `Password set for ${setPwTarget.email}. Share the new password with them out-of-band (SMS / WhatsApp).`,
      );
      setSetPwOpen(false);
      setSetPwValue("");
      setSetPwTarget(null);
      await runAudit();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSetPwBusy(false);
    }
  };

  const diagnosisColor = (d) => {
    switch (d) {
      case "single_user_ok":
        return "border-emerald-300 bg-emerald-50 text-emerald-900";
      case "no_user_docs":
        return "border-zinc-300 bg-zinc-50 text-zinc-900";
      case "multiple_user_docs":
      case "orphan_token":
      case "hash_malformed":
        return "border-red-300 bg-red-50 text-red-900";
      case "no_password_hash":
        return "border-amber-300 bg-amber-50 text-amber-900";
      default:
        return "border-zinc-300 bg-zinc-50 text-zinc-900";
    }
  };

  const diagnosisIcon = (d) => {
    if (d === "single_user_ok") return <CheckCircle2 className="h-5 w-5 shrink-0" />;
    if (d === "no_user_docs") return <XCircle className="h-5 w-5 shrink-0" />;
    return <AlertTriangle className="h-5 w-5 shrink-0" />;
  };

  return (
    <section className="mt-16" data-testid="admin-user-reset-audit-section">
      <div className="mb-4">
        <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950 inline-flex items-center gap-2">
          <Search className="h-5 w-5" /> User diagnostic lookup
        </h2>
        <p className="text-sm text-zinc-500 mt-1">
          Debug &quot;I clicked the reset link but still can&apos;t log
          in&quot;. Enter the user&apos;s email — the tool checks how many
          accounts exist under that email, whether their password is set,
          and whether their last reset token still points at a real user.
        </p>
      </div>

      <form onSubmit={runAudit} className="flex gap-3 items-end max-w-2xl">
        <div className="flex-1">
          <Label
            htmlFor="reset-audit-email"
            className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500"
          >
            User email
          </Label>
          <Input
            id="reset-audit-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            data-testid="admin-user-reset-audit-email"
            autoComplete="off"
            spellCheck={false}
            className="mt-2 h-11 rounded-sm border-zinc-300"
          />
        </div>
        <Button
          type="submit"
          disabled={loading}
          data-testid="admin-user-reset-audit-submit"
          className="h-11 rounded-sm bg-zinc-950 hover:bg-zinc-800 text-white font-semibold tracking-tight"
        >
          {loading ? "Checking…" : "Check user"}
        </Button>
      </form>

      {result && (
        <div className="mt-6" data-testid="admin-user-reset-audit-result">
          <div
            className={`rounded-sm border px-4 py-3 flex items-start gap-3 ${diagnosisColor(result.diagnosis)}`}
            data-testid="admin-user-reset-audit-diagnosis"
          >
            {diagnosisIcon(result.diagnosis)}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold uppercase tracking-[0.15em]">
                {result.diagnosis?.replace(/_/g, " ")}
              </div>
              <p className="mt-1 text-sm leading-snug">{result.headline}</p>
            </div>
          </div>

          <div className="mt-6">
            <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">
              User records ({result.match_count})
            </h3>
            {result.users.length === 0 ? (
              <p className="mt-2 text-sm text-zinc-500 italic">
                No accounts found. Ask the user to check for typos in
                their email address.
              </p>
            ) : (
              <div className="mt-2 space-y-3">
                {result.users.map((u) => (
                  <div
                    key={u.id}
                    className="rounded-sm border border-zinc-200 bg-white p-4"
                    data-testid={`admin-user-reset-audit-user-${u.id}`}
                  >
                    <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                      <div>
                        <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 block">
                          Email
                        </span>
                        <span className="font-mono">{u.email}</span>
                      </div>
                      <div>
                        <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 block">
                          Name
                        </span>
                        {u.name || <span className="text-zinc-400 italic">—</span>}
                      </div>
                      <div>
                        <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 block">
                          Role
                        </span>
                        {u.role}
                        {u.is_editor ? " · editor" : ""}
                      </div>
                      <div>
                        <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 block">
                          Pub / Client
                        </span>
                        {u.pub_slug || u.client_slug || (
                          <span className="text-zinc-400 italic">unassigned</span>
                        )}
                      </div>
                      <div>
                        <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 block">
                          Password
                        </span>
                        {u.has_password_hash ? (
                          u.password_hash_looks_like_bcrypt ? (
                            <span className="text-emerald-700 font-semibold">
                              set ({u.password_hash_starts_with}… · {u.password_hash_length} chars) ✓
                            </span>
                          ) : (
                            <span className="text-red-700 font-semibold">
                              malformed ({u.password_hash_starts_with}… · {u.password_hash_length} chars)
                            </span>
                          )
                        ) : (
                          <span className="text-amber-700 font-semibold">not set</span>
                        )}
                      </div>
                      <div>
                        <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 block">
                          Sessions invalidated
                        </span>
                        {u.token_version}× (via reset)
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => sendReset(u.id)}
                        disabled={sendResetBusy}
                        data-testid={`admin-user-reset-audit-send-${u.id}`}
                        className="h-9 rounded-sm border-zinc-300 text-zinc-800 font-semibold tracking-tight hover:border-zinc-950 hover:text-zinc-950"
                      >
                        <KeyRound className="h-3.5 w-3.5 mr-1.5" />
                        {sendResetBusy ? "Sending…" : "Send fresh reset link"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => openSetPw(u)}
                        data-testid={`admin-user-reset-audit-set-pw-${u.id}`}
                        className="h-9 rounded-sm border-amber-300 text-amber-800 font-semibold tracking-tight hover:border-amber-600 hover:bg-amber-50 hover:text-amber-900"
                      >
                        <Lock className="h-3.5 w-3.5 mr-1.5" />
                        Set password directly
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {result.reset_tokens.length > 0 && (
            <div className="mt-6">
              <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">
                Recent reset tokens (last {result.reset_tokens.length})
              </h3>
              <div className="mt-2 rounded-sm border border-zinc-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-xs font-bold uppercase tracking-[0.1em] text-zinc-600">
                    <tr>
                      <th className="text-left px-3 py-2">Token</th>
                      <th className="text-left px-3 py-2">Created</th>
                      <th className="text-left px-3 py-2">Used</th>
                      <th className="text-left px-3 py-2">User ID matches?</th>
                      <th className="text-left px-3 py-2">Issued by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.reset_tokens.map((t, idx) => (
                      <tr
                        key={idx}
                        className="border-t border-zinc-100"
                        data-testid={`admin-user-reset-audit-token-${idx}`}
                      >
                        <td className="px-3 py-2 font-mono text-xs">{t.token_suffix}</td>
                        <td className="px-3 py-2 text-xs">
                          {t.created_at
                            ? new Date(t.created_at).toLocaleString()
                            : "—"}
                          {t.expired ? (
                            <span className="ml-1 text-xs text-red-700">(expired)</span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2">
                          {t.used ? (
                            <span className="text-zinc-800">
                              ✓{" "}
                              {t.used_at ? new Date(t.used_at).toLocaleTimeString() : ""}
                            </span>
                          ) : (
                            <span className="text-zinc-400">no</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {t.user_id_matches_current_user_doc ? (
                            <span className="text-emerald-700 font-semibold">✓ yes</span>
                          ) : (
                            <span className="text-red-700 font-semibold">✗ orphan</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {t.issued_by_admin || (
                            <span className="text-zinc-400 italic">self-service</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      <Dialog open={setPwOpen} onOpenChange={setSetPwOpen}>
        <DialogContent data-testid="admin-user-set-pw-dialog">
          <DialogHeader>
            <DialogTitle>Set password directly</DialogTitle>
            <DialogDescription>
              Overwrites the user&apos;s stored password immediately. All
              their existing sessions will be signed out. You&apos;ll need
              to share the new password with the user by SMS, WhatsApp, or
              phone — this endpoint does NOT email it to them.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">
                Target user
              </Label>
              <p className="mt-1 text-sm font-mono">
                {setPwTarget?.email || ""}
              </p>
            </div>
            <div>
              <Label
                htmlFor="admin-set-pw-input"
                className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500"
              >
                New password (min 8 chars)
              </Label>
              <Input
                id="admin-set-pw-input"
                type="text"
                value={setPwValue}
                onChange={(e) => setSetPwValue(e.target.value)}
                placeholder="e.g. TempPass2026!"
                data-testid="admin-user-set-pw-input"
                autoComplete="off"
                spellCheck={false}
                className="mt-2 h-11 rounded-sm border-zinc-300"
              />
              <p className="mt-1 text-xs text-zinc-500">
                Type it visibly so you can copy-paste to the user.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSetPwOpen(false)}
              disabled={setPwBusy}
              data-testid="admin-user-set-pw-cancel"
              className="h-11 rounded-sm"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmSetPw}
              disabled={setPwBusy || setPwValue.length < 8}
              data-testid="admin-user-set-pw-confirm"
              className="h-11 rounded-sm bg-amber-600 hover:bg-amber-700 text-white font-semibold tracking-tight"
            >
              {setPwBusy ? "Saving…" : "Set password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
